/**
 * The comparison extension of the flat attribute leaf: a condition whose value
 * names another attribute, read when the condition is evaluated, instead of
 * holding a literal.
 *
 * On the wire that turns the plain `{ field, operator, value, inverse }` leaf
 * into an `attribute_compare` envelope carrying three more keys — `type`,
 * `field_source` and `value_source`. In row state it is one extra field,
 * `valueSource`, always present and `'value'` for the ordinary literal
 * comparison.
 *
 * Shared rather than per-surface because it describes bytes: `'const'` and a
 * missing `value_source` are the two wire spellings of the same static
 * comparison, and two surfaces reading them differently would disagree about
 * what a stored condition means. Which sources a surface may claim is not
 * shared — each format declares its own list, and the same declaration gates
 * both the parse claim and the dropdown, so the editor cannot claim a source
 * its select has no entry for.
 */

import { isRecord } from '@/utils/objects';
import type { AttributeFilterState } from '../attribute-filter-state';
import {
  flatAttributeBlockShape,
  parseFlatAttributeFilter,
  serializeFlatAttributeFilter,
  type FlatWireAttributeFilter,
  type WireAttributeBlock,
} from '../flat-attribute-filter';
import { isOperatorInFamilies, type OperatorFamily } from '../operators';

/**
 * A flat attribute filter plus what its value compares against.
 *
 * `valueSource` is always set: `'value'` is the literal comparison, and any
 * other source names the kind of attribute the value reads.
 */
export type ComparisonAttributeFilterState = AttributeFilterState & { valueSource: string };

/** The literal comparison, in row-state spelling. */
export const STATIC_VALUE_SOURCE = 'value';

/**
 * The block a comparison-capable row writes: the plain flat leaf, or that leaf
 * carrying the three envelope keys.
 *
 * One type for both because which of the two a row writes is a property of its
 * state, not of the caller — a literal comparison and a stale non-comparable
 * pairing both collapse to the plain leaf.
 */
export type FlatWireComparisonFilter =
  | FlatWireAttributeFilter
  | (FlatWireAttributeFilter & {
      type: 'attribute_compare';
      field_source: string;
      value_source: string;
    });

/**
 * The keys the compare envelope models: the flat leaf's own, plus the three the
 * envelope adds.
 *
 * `value2` is deliberately absent. No comparable operator takes two values and
 * no producer of this wire writes one, so a leaf carrying both a second value
 * and a compare envelope is a shape this codec cannot round-trip — declining it
 * leaves the stored bytes intact where claiming it would drop the extra on the
 * next save.
 */
const COMPARE_LEAF_KEYS: ReadonlySet<string> = new Set([
  'field',
  'operator',
  'value',
  'inverse',
  'type',
  'field_source',
  'value_source',
]);

/**
 * The operators a comparison can be built on.
 *
 * Only these appear behind the wire's other producer's comparison dropdown, so
 * a compare envelope on any other operator is not a condition either editor
 * could have written — and its own write path collapses such an envelope back
 * to a plain leaf, which is what {@link serializeFlatComparisonFilter}
 * reproduces.
 */
const COMPARABLE_OPERATORS: ReadonlySet<string> = new Set([
  'eq',
  '!eq',
  'gt',
  'lt',
  'contains',
  '!contains',
  'contained_in_array',
  '!contained_in_array',
]);

/** Whether an operator can carry a comparison against another attribute. */
export function isComparableOperator(operator: string): boolean {
  return COMPARABLE_OPERATORS.has(operator);
}

/**
 * The field_source spellings that read back as an editable attribute row.
 *
 * A surface in this wire family rewrites every one of them to its own fixed
 * source on write, which is what the wire's other producer does too. The
 * journey-attribute source is deliberately absent: that producer routes it to a
 * different condition type altogether, so a surface claiming it would need that
 * row first.
 */
const CLAIMED_FIELD_SOURCES: ReadonlySet<string> = new Set([
  'event_attribute',
  'customer_attribute',
  'foreign_event_attribute',
]);

/**
 * The block's operator in row-state spelling, where an `inverse` block reads as
 * the `!`-prefixed operator the row models.
 */
function compareUiOperator(block: WireAttributeBlock): string {
  const operator = String(block.operator ?? '');
  return block.inverse === true ? `!${operator}` : operator;
}

/**
 * Whether the node is an `attribute_compare` envelope this surface can edit.
 *
 * Every gate is a reason the row could not otherwise be shown faithfully: an
 * unmodelled key would be dropped on save, an operator outside the surface's
 * families or outside the comparable set would render with no editor for it, and
 * a source the surface does not declare would load into a dropdown with no
 * matching entry. Anything declined rides the passthrough with its bytes
 * intact.
 */
export function isSupportedFlatComparisonFilter(
  json: unknown,
  families: readonly OperatorFamily[],
  valueSources: readonly string[],
): boolean {
  const block = flatAttributeBlockShape(json, COMPARE_LEAF_KEYS);
  if (block == null || block.type !== 'attribute_compare') {
    return false;
  }
  if (!('value' in block) || (block.value !== null && typeof block.value !== 'string')) {
    return false;
  }
  const operator = compareUiOperator(block);
  if (!isComparableOperator(operator) || !isOperatorInFamilies(operator, new Set(families))) {
    return false;
  }
  if (typeof block.field_source !== 'string' || !CLAIMED_FIELD_SOURCES.has(block.field_source)) {
    return false;
  }
  const source = block.value_source;
  if (source === undefined || source === 'const') {
    return true;
  }
  return typeof source === 'string' && valueSources.includes(source);
}

/**
 * The row state for a compare envelope. Call only behind
 * {@link isSupportedFlatComparisonFilter}: a throw escaping a plugin's `parse`
 * is a plugin bug under dryad's contract, so a plugin declines with `null`
 * first.
 *
 * Both wire spellings of the literal comparison read as `'value'`, which is
 * what lets a condition saved without a `value_source` preselect its dropdown
 * entry instead of loading with the select empty.
 */
export function parseFlatComparisonFilter(json: unknown): ComparisonAttributeFilterState {
  if (!isRecord(json) || json.type !== 'attribute_compare') {
    throw new Error(`Not an attribute_compare filter: ${JSON.stringify(json)}`);
  }
  const source = json.value_source;
  const valueSource = typeof source === 'string' && source !== 'const' ? source : STATIC_VALUE_SOURCE;
  return { ...parseFlatAttributeFilter(json, COMPARE_LEAF_KEYS), valueSource };
}

/**
 * The wire block for comparison-capable row state.
 *
 * A literal comparison writes the plain flat leaf, byte-identical to a row that
 * never used a comparison at all. So does a non-literal source left behind on
 * an operator that cannot compare — a stale pairing the dropdown can produce by
 * changing the operator after the source, and one the wire's other producer
 * collapses the same way.
 *
 * `fieldSource` is the surface's own fixed source, which every claimed spelling
 * is rewritten to. That rewrite is lossy and deliberate: it is what the wire's
 * other producer writes, so a condition saved from here stays byte-identical to
 * the same condition saved from there.
 */
export function serializeFlatComparisonFilter(
  state: ComparisonAttributeFilterState,
  fieldSource: string,
): FlatWireComparisonFilter {
  const flat = serializeFlatAttributeFilter(state);
  if (state.valueSource === STATIC_VALUE_SOURCE || !isComparableOperator(state.operator)) {
    return flat;
  }
  return { ...flat, type: 'attribute_compare', field_source: fieldSource, value_source: state.valueSource };
}
